import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ArrowRight, Mail } from "lucide-react";

export default function ContactPreview() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-10 sm:py-14 lg:py-16 bg-white overflow-hidden"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`border border-gray-100 rounded-2xl overflow-hidden transition-all duration-700 ease-out ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          {/* Top accent bar */}
          <div className="h-1 w-full bg-black" />

          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Left — message */}
            <div className="p-8 md:p-10 lg:p-12 flex flex-col justify-between border-b md:border-b-0 md:border-r border-gray-100">
              <div>
                <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 px-4 py-1.5 rounded-full mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                  <span className="text-blue-600 text-xs sm:text-sm font-medium tracking-wide">
                    Get in touch
                  </span>
                </div>
                <h2 className="text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-medium text-black mb-4 leading-tight">
                  Let's build something{" "}
                  <span className="text-blue-600">great</span>
                </h2>
                <p className="text-gray-500 text-sm sm:text-base md:text-lg leading-relaxed mb-8">
                  Have a project in mind or just want to say hi? I'd love to
                  hear from you. Let's build something great together.
                </p>
              </div>
              <Link
                to="/contact"
                className="group self-start inline-flex items-center gap-2 bg-black text-white text-sm md:text-base font-medium px-6 py-3 rounded-full hover:bg-blue-600 transition-colors duration-200"
              >
                Send a message
                <ArrowRight
                  size={15}
                  className="group-hover:translate-x-0.5 transition-transform"
                />
              </Link>
            </div>

            {/* Right — details */}
            <div className="p-8 md:p-10 lg:p-12 bg-gray-50 flex flex-col justify-center gap-8">
              <div>
                <p className="text-[10px] sm:text-[11px] font-medium text-gray-400 tracking-widest uppercase mb-3">
                  What I can help with
                </p>
                <div className="flex flex-col gap-2.5">
                  {[
                    "Full-stack web development",
                    "API design & integration",
                    "Database architecture",
                    "Payment systems (M-Pesa & more)",
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                      <span className="text-sm md:text-[15px] text-gray-600">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-200 pt-8">
                <p className="text-[10px] sm:text-[11px] font-medium text-gray-400 tracking-widest uppercase mb-3">
                  Response time
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
                    <Mail size={14} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm md:text-[15px] font-medium text-black">
                      Usually within 24 hours
                    </p>
                    <p className="text-xs md:text-sm text-gray-400">
                      Open to freelance & full-time roles
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
